import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import PageHeader from "@/components/PageHeader";
import PermissionGate from "@/components/PermissionGate";
import EnrollmentDialog from "@/components/enrollments/EnrollmentDialog";
import FinanceSheet from "@/components/enrollments/FinanceSheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, Search, MoreHorizontal, GraduationCap, Wallet } from "lucide-react";

const ACADEMIC = ["Enrolled", "In Progress", "Completed", "Dropped", "On Hold"];
const PAY_STYLE = { Pending: "bg-amber-100 text-amber-700", Partial: "bg-blue-100 text-blue-700", Paid: "bg-accent/10 text-accent", Overdue: "bg-red-100 text-red-600", Refunded: "bg-slate-100 text-slate-600" };
const ACAD_STYLE = { Enrolled: "bg-blue-100 text-blue-700", "In Progress": "bg-primary/10 text-primary", Completed: "bg-accent/10 text-accent", Dropped: "bg-red-100 text-red-600", "On Hold": "bg-slate-100 text-slate-600" };
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export default function Enrollments() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [acadFilter, setAcadFilter] = useState("all");
  const [payFilter, setPayFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [financeRow, setFinanceRow] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/enrollments", {
      params: { search: search || undefined, academic_status: acadFilter, payment_status: payFilter, page_size: 100 },
    });
    setRows(data.items || []);
    setTotal(data.total || 0);
  }, [search, acadFilter, payFilter]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditRow(null); setDialogOpen(true); };
  const openEdit = (e) => { setEditRow(e); setDialogOpen(true); };

  const changeStatus = async (e, status) => {
    try { await api.patch(`/enrollments/${e.id}/status`, { academic_status: status }); toast.success(`Marked ${status}`); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (e) => {
    if (!window.confirm(`Delete enrollment ${e.enrollment_code}?`)) return;
    try { await api.delete(`/enrollments/${e.id}`); toast.success("Enrollment deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="enrollments-page">
      <PageHeader title="Enrollments" subtitle="The heart of the institute — every admission lives here." />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search by code, student, course..." value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 w-72 pl-9" data-testid="enrollment-search" />
          </div>
          <Select value={acadFilter} onValueChange={setAcadFilter}>
            <SelectTrigger className="h-9 w-40" data-testid="enrollment-academic-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All statuses</SelectItem>{ACADEMIC.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={payFilter} onValueChange={setPayFilter}>
            <SelectTrigger className="h-9 w-40" data-testid="enrollment-payment-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All payments</SelectItem>{["Pending", "Partial", "Paid", "Overdue", "Refunded"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <PermissionGate permission="enrollment.create">
          <Button onClick={openNew} data-testid="add-enrollment-button"><Plus className="mr-1.5 h-4 w-4" /> New enrollment</Button>
        </PermissionGate>
      </div>

      <div className="rounded-xl border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead><TableHead>Student</TableHead><TableHead>Course / Batch</TableHead>
              <TableHead>Final Fee</TableHead><TableHead>Pending</TableHead><TableHead>Payment</TableHead>
              <TableHead>Academic</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={8} className="py-10 text-center text-muted-foreground">No enrollments yet. Click “New enrollment” to admit a student.</TableCell></TableRow>}
            {rows.map((e) => (
              <TableRow key={e.id} data-testid={`enrollment-row-${e.enrollment_code}`}>
                <TableCell className="font-medium">{e.enrollment_code}</TableCell>
                <TableCell className="font-medium text-secondary">{e.student_name}<br /><span className="text-xs text-muted-foreground">{e.student_code}</span></TableCell>
                <TableCell><span className="flex items-center gap-1.5 text-sm"><GraduationCap className="h-4 w-4 text-primary" />{e.course_name}</span><span className="text-xs text-muted-foreground">{e.batch_name || "No batch"}</span></TableCell>
                <TableCell>{inr(e.final_fee)}</TableCell>
                <TableCell className={e.pending_amount > 0 ? "font-medium text-red-600" : "text-accent"}>{inr(e.pending_amount)}</TableCell>
                <TableCell><Badge className={`border-0 ${PAY_STYLE[e.payment_status] || ""}`}>{e.payment_status}</Badge></TableCell>
                <TableCell><Badge className={`border-0 ${ACAD_STYLE[e.academic_status] || ""}`}>{e.academic_status}</Badge></TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" data-testid={`enrollment-menu-${e.enrollment_code}`}><MoreHorizontal className="h-4 w-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <PermissionGate permission="payment.read">
                        <DropdownMenuItem onClick={() => setFinanceRow(e)} data-testid={`manage-fees-${e.enrollment_code}`}><Wallet className="mr-2 h-4 w-4" /> Manage fees</DropdownMenuItem>
                      </PermissionGate>
                      <PermissionGate permission="enrollment.update">
                        <DropdownMenuItem onClick={() => openEdit(e)}><Pencil className="mr-2 h-4 w-4" /> Edit</DropdownMenuItem>
                        {ACADEMIC.filter((s) => s !== e.academic_status).map((s) => (
                          <DropdownMenuItem key={s} onClick={() => changeStatus(e, s)}>Mark {s}</DropdownMenuItem>
                        ))}
                      </PermissionGate>
                      <PermissionGate permission="enrollment.delete">
                        <DropdownMenuItem className="text-destructive" onClick={() => remove(e)}><Trash2 className="mr-2 h-4 w-4" /> Delete</DropdownMenuItem>
                      </PermissionGate>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <p className="mt-3 text-sm text-muted-foreground">{total} enrollment{total === 1 ? "" : "s"}</p>

      <EnrollmentDialog open={dialogOpen} onOpenChange={setDialogOpen} editRow={editRow} onSaved={load} />
      <FinanceSheet enrollment={financeRow} open={!!financeRow} onOpenChange={(v) => !v && setFinanceRow(null)} onChanged={load} />
    </div>
  );
}
